import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  getUserInfo,
  getVersionInfo,
  logoutAuthProfile,
  setAuthProfile,
  updateAuthUserSelectedTenant,
  setVersionDetails,
} from '../slices/auth.slice'
import { clearSession, setSessionCredentials } from '../slices/session.slice'
import { SagaIterator } from 'redux-saga'
import {
  getClientByTenantId,
  getLoggingActions,
  getSiteDetails,
  getUserLoggedInMsal,
  getVersionOfApplication,
} from '../apis/user'
import { AuthUser, AuthUserClient, UserAuthPayload } from '../types/auth.types'
import { PayloadAction } from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import {
  navigateTo,
  getPermissionByTenantId,
  updateClientPermissionState,
  showLoader,
  hideLoader,
  resetCategoryDropdownData,
} from '../slices'
import { AUTH_PROVIDER, getAuthProviderType } from '../utils/environment'
import { LoginTypeEnum } from '../types'
import { isEqual } from 'lodash'

function* getAuthUserData(
  action: PayloadAction<UserAuthPayload>
): SagaIterator {
  try {
    let response
    if (getAuthProviderType() === AUTH_PROVIDER.MSAL) {
      response = yield call<
        (
          token: string,
          source?: string,
          user_type?: string
        ) => Promise<AxiosResponse<AuthUser>>
      >(
        getUserLoggedInMsal,
        action.payload.token,
        action.payload.source,
        action.payload.user_type
      )
    } else {
      response = yield call(getClientByTenantId, action.payload.token)
    }

    const userData: AuthUser = response.data

    if (userData?.profile === 'Super Admin') {
      userData.profile = 'System Admin'
    }

    if (userData.enabled) {
      const prevClient = yield select(
        (state) => state.authUser.selectedClientTenant
      )
      const prevSessionLoginAtMs: number = yield select(
        (state) => state.session.sessionLoginAtMs ?? 0
      )
      const sessionLoginAtMs =
        action.payload.source === LoginTypeEnum.LOGIN
          ? Date.now()
          : prevSessionLoginAtMs
      if (userData.superAdmin) {
        const globalClient: AuthUserClient = {
          enabled: true,
          id: -1,
          name_of_organization: 'Global',
          schema_name: 'global',
          status: true,
        }
        const allClient: AuthUserClient[] = [globalClient, ...userData.clients]
        userData.clients = allClient
        const newClient =
          allClient.find((client) => isEqual(client, prevClient)) ??
          (allClient?.length > 1 ? allClient[1] : globalClient)

        yield put(
          setAuthProfile({
            user: userData,
            selectedClientTenant: newClient,
          })
        )
        yield put(
          setSessionCredentials({
            isAuthenticated: true,
            token: action.payload.token,
            sessionLoginAtMs,
            ...(action.payload.source === LoginTypeEnum.LOGIN
              ? { maxSessionExtensionCount: 0 }
              : {}),
          })
        )
        yield put(getVersionInfo({ token: action.payload.token }))
      } else {
        const allClient: AuthUserClient[] = userData?.clients?.filter(
          (client) => client.enabled
        )
        if (allClient && allClient.length > 0) {
          const newClient =
            allClient.find((client) => isEqual(client, prevClient)) ??
            allClient[0]
          yield put(
            setAuthProfile({
              user: { ...response.data },
              selectedClientTenant: newClient,
            })
          )
          yield put(
            setSessionCredentials({
              isAuthenticated: true,
              token: action.payload.token,
              sessionLoginAtMs,
              ...(action.payload.source === LoginTypeEnum.LOGIN
                ? { maxSessionExtensionCount: 0 }
                : {}),
            })
          )
          yield put(getVersionInfo({ token: action.payload.token }))
        } else {
          console.error('User client not assigned')
          yield call(
            getLoggingActions,
            action.payload.token,
            LoginTypeEnum.UNAUTHORIZED,
            action.payload.user_type
          )
          yield put(logoutAuthProfile())
          yield put(clearSession())
          yield put(navigateTo(`/error/user-not-found`))
        }
      }
    } else {
      console.error('User disabled')
      yield call(
        getLoggingActions,
        action.payload.token,
        LoginTypeEnum.UNAUTHORIZED,
        action.payload.user_type
      )
      yield put(logoutAuthProfile())
      yield put(clearSession())
      yield put(navigateTo(`/error/user-not-found`))
    }
  } catch (err) {
    const error: AxiosError = err as AxiosError
    if (
      error &&
      error.response?.status &&
      (error.response?.status == 401 || error.response?.status == 409)
    ) {
      // yield put(logoutAuthUser());
      yield put(navigateTo(`/error/user-not-found`))
      return
    } else if (
      error &&
      error.response?.status &&
      error.response?.status == 500
    ) {
      // yield put(logoutAuthUser());
      yield put(navigateTo(`/error/user-error`))
      return
    } else {
      yield put(navigateTo(`/error/user-error`))
      console.log('fetchClientsByTenant err', error.response?.status)
      yield call(
        getLoggingActions,
        action.payload.token,
        LoginTypeEnum.UNAUTHORIZED,
        action.payload.user_type
      )
      yield put(logoutAuthProfile())
      yield put(clearSession())
    }
  }
}

function* getClientPermissions(action: PayloadAction<number>): SagaIterator {
  try {
    yield put(showLoader())
    yield put(resetCategoryDropdownData())
    const isSuperAdmin = yield select((state) => state.authUser.user.superAdmin)
    if (!isSuperAdmin) {
      yield put(getPermissionByTenantId(action.payload))
    } else {
      localStorage.setItem('has_sensitive_permission', 'true')
      yield put(
        updateClientPermissionState({
          programPermissions: {},
          globalPermissions: {},
          hasViewPermission: true,
          programs: {},
          permissionServices: {},
          programServices: {},
        })
      )
    }
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

function* getVersionDetails(
  action: PayloadAction<UserAuthPayload>
): SagaIterator {
  try {
    yield put(showLoader())
    const isShowVersion = yield call(getSiteDetails, action.payload.token)
    if (isShowVersion?.data?.[0]?.value) {
      const response = yield call(getVersionOfApplication, action.payload.token)
      const data = response.data ?? {}
      const version = data?.app?.version ? `v${data.app.version}` : null
      const build = data?.app?.iteration ?? null
      const envLabel = data?.app?.env ? data.app.env.toUpperCase() : null
      const formatted = [version, build, envLabel].filter(Boolean).join(' • ')
      yield put(setVersionDetails(formatted))
    }
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

export function* watchGetAuthUserData() {
  yield takeLatest(getUserInfo, getAuthUserData)
}

export function* watchGetClientPermissions() {
  yield takeLatest(updateAuthUserSelectedTenant, getClientPermissions)
}

export function* watchGetVersionDetails() {
  yield takeLatest(getVersionInfo, getVersionDetails)
}
